import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import useTheme from "hooks/useTheme";

const AVATARS = [
  "/creators/kirti-gupta-avatar.jpg",
  "/creators/sehej-bhasin-avatar.webp",
  "/creators/varad-kulkarni-avatar.jpg",
  "/creators/gautam-jagdev-avatar.jpg",
];

/**
 * Split-screen layout shared by Sign in / Sign up / Forgot password.
 */
export const AuthLayout = ({ eyebrow, title, subtitle, children, wide }) => {
  const { theme } = useTheme();
  const logoSrc =
    theme === "light" ? "/qreativs-logo.png" : "/qreativs-logo-dark.png";

  return (
    <div className="auth-root landing-root min-h-screen bg-zinc-950 text-zinc-50 font-sans-body flex flex-col lg:flex-row">
      <div className="relative hidden lg:flex lg:w-[46%] xl:w-[42%] overflow-hidden border-r border-zinc-900">
        <div className="absolute inset-0 grid-lines opacity-40" aria-hidden />
        <div
          className="absolute -left-24 top-1/3 h-[420px] w-[420px] rounded-full blur-3xl opacity-50"
          style={{
            background:
              "radial-gradient(circle, rgba(108,99,245,0.35), transparent 70%)",
          }}
          aria-hidden
        />
        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full">
          <Link to="/" className="inline-flex">
            <img
              src={logoSrc}
              alt="Qreativs"
              className="h-8 w-auto"
              draggable={false}
            />
          </Link>

          <div className="max-w-md">
            <h2 className="font-display font-bold text-4xl xl:text-5xl leading-[1.02] tracking-tighter text-zinc-50">
              Welcome to your
              <br />
              <span className="text-[#6C63F5]">creative network.</span>
            </h2>
            <p className="mt-6 text-zinc-400 leading-relaxed">
              This is where it all begins — with thousands of creatives and brand
              users.
            </p>

            <div className="mt-8 flex items-center gap-3">
              <div className="flex -space-x-3">
                {AVATARS.map((a) => (
                  <img
                    key={a}
                    src={a}
                    alt=""
                    className="h-10 w-10 rounded-full object-cover border-2 border-zinc-950"
                  />
                ))}
              </div>
              <span className="text-sm text-zinc-400">
                <span className="text-zinc-100 font-semibold">5,000+</span>{" "}
                creatives already inside
              </span>
            </div>
          </div>

          <div className="text-xs text-zinc-600">
            © {new Date().getFullYear()} Qreativs.
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col">
        <div className="lg:hidden px-6 pt-8">
          <Link to="/" className="inline-flex">
            <img
              src={logoSrc}
              alt="Qreativs"
              className="h-7 w-auto"
              draggable={false}
            />
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className={`w-full ${wide ? "max-w-lg" : "max-w-md"}`}
          >
            {eyebrow && (
              <div className="text-xs uppercase tracking-[0.2em] text-[#6C63F5] font-medium mb-3">
                {eyebrow}
              </div>
            )}
            <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter text-zinc-50">
              {title}
            </h1>
            {subtitle && <p className="mt-3 text-zinc-400">{subtitle}</p>}

            <div className="mt-8">{children}</div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export const authInputClass =
  "w-full rounded-xl bg-zinc-900/70 border border-zinc-800 text-zinc-100 placeholder:text-zinc-500 px-4 py-3 text-sm focus:outline-none focus:border-[#6C63F5] focus:ring-2 focus:ring-[#6C63F5]/20 transition-[border-color,box-shadow]";

export const authPrimaryBtnClass =
  "group w-full inline-flex items-center justify-center gap-2 bg-[#6C63F5] text-white hover:bg-[#5751E5] rounded-full px-6 py-3.5 text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed";

export const authSecondaryBtnClass =
  "w-full inline-flex items-center justify-center gap-3 bg-transparent border border-zinc-800 text-zinc-100 hover:border-zinc-600 hover:bg-zinc-900/50 rounded-full px-6 py-3.5 text-sm font-medium transition-colors";

export const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
    <path
      fill="#FFC107"
      d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1.1 7.4 2.8l5.7-5.7C33.6 6.9 29 5 24 5 13.5 5 5 13.5 5 24s8.5 19 19 19 19-8.5 19-19c0-1.3-.1-2.3-.4-3.5z"
    />
    <path
      fill="#FF3D00"
      d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c2.8 0 5.4 1.1 7.4 2.8l5.7-5.7C33.6 6.9 29 5 24 5 16.3 5 9.7 9.3 6.3 14.7z"
    />
    <path
      fill="#4CAF50"
      d="M24 43c5.2 0 9.8-1.9 13.3-5.1l-6.2-5.2C29.1 34.1 26.7 35 24 35c-5.3 0-9.7-2.6-11.3-6.9l-6.5 5C9.6 38.6 16.2 43 24 43z"
    />
    <path
      fill="#1976D2"
      d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.6l6.2 5.2C41.6 35.9 43 30.4 43 24c0-1.3-.1-2.3-.4-3.5z"
    />
  </svg>
);

export default AuthLayout;
